import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';

const Products = () => {
  // States for product management
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currentProduct, setCurrentProduct] = useState({
    id: null,
    name: '',
    description: '',
    sku: '',
    unitType: 'piece', // Default unit type
    unitPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    supplierId: '',
    isActive: true
  });
  
  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterStock, setFilterStock] = useState('all'); // 'all', 'low', 'out'

  // Unit type options
  const unitTypeOptions = [
    { value: 'piece', label: 'Piece' },
    { value: 'kg', label: 'Kilogram' },
    { value: 'l', label: 'Liter' },
    { value: 'g', label: 'Gram' },
    { value: 'ml', label: 'Milliliter' },
    { value: 'box', label: 'Box' },
    { value: 'pack', label: 'Pack' },
    { value: 'batch', label: 'Batch' }
  ];

  // Fetch products with filters
  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Prepare filter parameters
      const filters = {};
      if (searchTerm) filters.name = searchTerm;
      if (filterSupplier) filters.supplierId = filterSupplier;
      if (filterActive !== 'all') filters.isActive = filterActive === 'active';
      if (filterStock === 'low') filters.lowStock = true;
      if (filterStock === 'out') filters.outOfStock = true;

      console.log('Loading products...', filters);
      const response = await apiService.getProducts(filters);
      console.log('Products response:', response);
      setProducts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products: ' + 
        (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers for dropdown
  const fetchSuppliers = async () => {
    try {
      const response = await apiService.getSuppliers({ isActive: true });
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      // No need to set error as this is secondary data
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, []);

  // Reload products when filters change
  useEffect(() => {
    fetchProducts();
  }, [searchTerm, filterSupplier, filterActive, filterStock]);

  // Handle product form input changes
const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // For number inputs, validate and convert to number or null
    if (name === 'unitPrice' || name === 'stockQuantity' || name === 'minStockLevel') {
      const numValue = value === '' ? null : (type === 'number' ? parseFloat(value) : value);
      setCurrentProduct({
        ...currentProduct,
        [name]: numValue
      });
    } else {
      setCurrentProduct({
        ...currentProduct,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };


  // Submit product form
  // Submit product form
// Submit product form
const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Validate form
    if (!currentProduct.supplierId) {
      setError('Please select a supplier');
      return;
    }
  
    // Create a copy of the product data with proper field mapping for API
    const productToSubmit = {
      id: currentProduct.id,
      name: currentProduct.name,
      description: currentProduct.description || null,
      sku: currentProduct.sku || null,
      unit: currentProduct.unitType, // Map 'unitType' in form to 'unit' for API
      unitPrice: currentProduct.unitPrice === '' ? null : currentProduct.unitPrice,
      currentStock: currentProduct.stockQuantity === '' ? null : currentProduct.stockQuantity, // Map 'stockQuantity' to 'currentStock' for API
      minStockLevel: currentProduct.minStockLevel === '' ? null : currentProduct.minStockLevel,
      supplierId: currentProduct.supplierId,
      isActive: currentProduct.isActive
    };
  
    try {
      if (modalMode === 'add') {
        // Create new product
        await apiService.createProduct(productToSubmit);
      } else {
        // Update existing product
        await apiService.updateProduct(productToSubmit.id, productToSubmit);
      }
  
      // Success actions
      setModalVisible(false);
      resetForm();
      fetchProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      setError('Failed to save product: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Delete product
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await apiService.deleteProduct(id);
      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product: ' + 
        (err.response?.data?.error || err.message));
    }
  };

// Update stock
const handleUpdateStock = async (product, newQuantity) => {
    try {
      await apiService.updateProductStock(product.id, { currentStock: newQuantity }); // Use 'currentStock' instead of 'stockQuantity'
      fetchProducts();
    } catch (err) {
      console.error('Error updating stock:', err);
      setError('Failed to update stock: ' + 
        (err.response?.data?.error || err.message));
    }
  };
// Edit product
const handleEdit = (product) => {
    setCurrentProduct({
      id: product.id,
      name: product.name,
      description: product.description || '',
      sku: product.sku || '',
      unitType: product.unit || 'piece', // Map 'unit' from API to 'unitType' in form
      unitPrice: product.unitPrice !== null ? parseFloat(product.unitPrice) : null,
      stockQuantity: product.currentStock !== null ? parseFloat(product.currentStock) : null, // Map 'currentStock' to 'stockQuantity'
      minStockLevel: product.minStockLevel !== null ? parseFloat(product.minStockLevel) : null,
      supplierId: product.supplierId,
      isActive: product.isActive
    });
    
    setModalMode('edit');
    setModalVisible(true);
  };



  // Open add product modal
  const handleAdd = () => {
    resetForm();
    setModalMode('add');
    setModalVisible(true);
  };

  // Reset product form
  const resetForm = () => {
    setCurrentProduct({
      id: null,
      name: '',
      description: '',
      sku: '',
      unitType: 'piece',
      unitPrice: '',
      stockQuantity: '',
      minStockLevel: '',
      supplierId: '',
      isActive: true
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '-';
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format stock status with color coding
  const getStockStatusDisplay = (product) => {
    const stockQuantity = parseFloat(product.currentStock) || 0;
    const minLevel = parseFloat(product.minStockLevel) || 0;
    
    if (stockQuantity <= 0) {
      return <span className="status-badge stock-out">Out of Stock</span>;
    } else if (minLevel > 0 && stockQuantity <= minLevel) {
      return <span className="status-badge stock-low">Low Stock ({stockQuantity} {product.unit})</span>;
    } else {
      return <span className="status-badge stock-ok">{stockQuantity} {product.unit}</span>;
    }
  };

  // Get supplier name by ID
  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : '-';
  };

  return (
    <div className="tab-content">
      <div className="content-header">
        <h3>Products</h3>
        <button 
          className="add-button"
          onClick={handleAdd}
        >
          <span className="icon">+</span>
          <span className="button-text">Add New Product</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Search products..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <label htmlFor="filterSupplier">Supplier:</label>
          <select 
            id="filterSupplier" 
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
          >
            <option value="">All</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterStock">Stock:</label>
          <select 
            id="filterStock" 
            value={filterStock}
            onChange={(e) => setFilterStock(e.target.value)}
          >
            <option value="all">All</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterActive">Status:</label>
          <select 
            id="filterActive" 
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="products-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>SKU</th>
                <th>Supplier</th>
                <th>Unit Price</th>
                <th>Unit</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">No products found.</td>
                </tr>
              ) : (
                products.map(product => (
                  <tr key={product.id} className={product.isActive ? 'active-row' : 'inactive-row'}>
                    <td>{product.name}</td>
                    <td>{product.sku || '-'}</td>
                    <td>{getSupplierName(product.supplierId)}</td>
                    <td>{formatCurrency(product.unitPrice)}</td>
                    <td>{product.unitType}</td>
                    <td>{getStockStatusDisplay(product)}</td>
                    <td>
                      <span className={`status-badge ${product.isActive ? 'active' : 'inactive'}`}>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-button"
                          onClick={() => handleEdit(product)}
                          title="Edit"
                        >
                          <span className="icon">✎</span>
                        </button>
                        <button 
                          className="stock-button"
                          onClick={() => {
                            const newQuantity = window.prompt(
                              `Update stock quantity for ${product.name}:`, 
                              product.stockQuantity
                            );
                            if (newQuantity !== null) {
                              handleUpdateStock(product, parseFloat(newQuantity));
                            }
                          }}
                          title="Update Stock"
                        >
                          <span className="icon">⟳</span>
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(product.id)}
                          title="Delete"
                        >
                          <span className="icon">✕</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Product Modal */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{modalMode === 'add' ? 'Add New Product' : 'Edit Product'}</h3>
              <button 
                className="close-button"
                onClick={() => setModalVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <form className="form" onSubmit={handleSubmit}>
              <div className="form-section">
                <h4>Basic Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Product Name *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={currentProduct.name} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="sku">SKU</label>
                    <input 
                      type="text" 
                      id="sku" 
                      name="sku" 
                      value={currentProduct.sku} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea 
                    id="description" 
                    name="description" 
                    value={currentProduct.description} 
                    onChange={handleInputChange} 
                    rows="3"
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="supplierId">Supplier *</label>
                  <select 
                    id="supplierId" 
                    name="supplierId" 
                    value={currentProduct.supplierId} 
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="isActive" 
                      checked={currentProduct.isActive} 
                      onChange={handleInputChange} 
                    />
                    Active
                  </label>
                </div>
              </div>
              
              <div className="form-section">
                <h4>Pricing & Stock</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="unitPrice">Unit Price</label>
                    <input 
                      type="number" 
                      id="unitPrice" 
                      name="unitPrice" 
                      value={currentProduct.unitPrice} 
                      onChange={handleInputChange} 
                      step="0.01"
                      min="0"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="unitType">Unit Type</label>
                    <select 
                      id="unitType" 
                      name="unitType" 
                      value={currentProduct.unitType} 
                      onChange={handleInputChange}
                    >
                      {unitTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="stockQuantity">Stock Quantity</label>
                    <input 
                      type="number" 
                      id="stockQuantity" 
                      name="stockQuantity" 
                      value={currentProduct.stockQuantity} 
                      onChange={handleInputChange} 
                      step="0.01"
                      min="0"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="minStockLevel">Minimum Stock Level</label>
                    <input 
                      type="number" 
                      id="minStockLevel" 
                      name="minStockLevel" 
                      value={currentProduct.minStockLevel} 
                      onChange={handleInputChange} 
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setModalVisible(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;